import PropTypes from 'prop-types';
import { forwardRef } from 'react';

import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import { styled } from '@mui/material/styles';
import ListItemButton from '@mui/material/ListItemButton';

import { RouterLink } from 'src/routes/components';

import Iconify from 'src/components/iconify';

// ----------------------------------------------------------------------

const NavItem = forwardRef(
  ({ title, path, icon, open, active, hasChild, externalLink, ...other }, ref) => {
    const renderContent = (
      <StyledNavItem
        disableRipple
        disableTouchRipple
        ref={ref}
        open={open}
        active={active}
        showPointer={path || hasChild}
        {...other}
      >
        {icon && (
          <Box component="span" className="icon">
            {icon}
          </Box>
        )}

        {title && (
          <Box component="span" className="label">
            {title}
          </Box>
        )}

        {hasChild && <Iconify width={16} className="arrow" icon="eva:arrow-ios-downward-fill" />}
      </StyledNavItem>
    );

    if (path && externalLink)
      return (
        <Link href={path} target="_blank" rel="noopener" underline="none" color="inherit">
          {renderContent}
        </Link>
      );

    if (path)
      return (
        <Link component={RouterLink} href={path} underline="none" color="inherit">
          {renderContent}
        </Link>
      );

    return renderContent;
  }
);

NavItem.propTypes = {
  open: PropTypes.bool,
  active: PropTypes.bool,
  path: PropTypes.string,
  icon: PropTypes.element,
  title: PropTypes.string,
  hasChild: PropTypes.bool,
  externalLink: PropTypes.bool,
};

export default NavItem;

// ----------------------------------------------------------------------

const StyledNavItem = styled(ListItemButton, {
  shouldForwardProp: (prop) => prop !== 'active' && prop !== 'showPointer',
})(({ active, open, theme, showPointer }) => {
  const opened = open && !active;

  return {
    ...theme.typography.body2,
    padding: 0,
    minHeight: '100%',
    cursor: 'unset',
    fontWeight: theme.typography.fontWeightMedium,
    transition: theme.transitions.create(['all'], {
      duration: theme.transitions.duration.shorter,
    }),
    '&:hover': {
      backgroundColor: 'transparent',
    },
    '& .icon': {
      width: 20,
      height: 20,
      flexShrink: 0,
      marginRight: theme.spacing(1),
    },
    '& .arrow': {
      marginLeft: theme.spacing(0.75),
    },
    ...(active && {
      color: theme.palette.primary.main,
      fontWeight: theme.typography.fontWeightSemiBold,
    }),
    ...(showPointer && {
      cursor: 'pointer',
    }),
    ...(opened && {
      opacity: 0.64,
    }),
  };
});
